import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import logger from "../utils/logger";

/**
 * Custom hook for managing authentication state
 * Provides a centralized way to track authentication status and handle state changes
 */
export const useAuthState = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  
  // Note: Profile context integration removed to avoid hook dependency issues
  // Components should use useProfile hook directly when needed

  // Check authentication status
  const checkAuthStatus = useCallback(() => {
    if (!instance) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      // BEST PRACTICE: Use accounts array from useMsal hook instead of getAllAccounts()
      // Trust the hook - it provides the authoritative account list
      const hasAccounts = accounts && accounts.length > 0 && inProgress === 'none';
      
      setIsAuthenticated(hasAccounts);
      
      if (hasAccounts) {
        const account = accounts[0];
        
        // For Entra External ID, check idTokenClaims for email information
        const idTokenClaims = account.idTokenClaims || {};
        
        // Extract email - avoid using 'sub' claim as it's often a cryptic ID
        let email = idTokenClaims.email || 
                   idTokenClaims.preferred_username || 
                   idTokenClaims.upn ||
                   account.username || 
                   account.preferred_username || 
                   account.email || '';
        
        // Only use 'sub' claim if it looks like an email (contains @)
        if (!email && idTokenClaims.sub && idTokenClaims.sub.includes('@')) {
          email = idTokenClaims.sub;
        }
        
        // If still no email found, show a placeholder
        if (!email) {
          email = 'Email not available';
        }
        
        setUser({
          name: account.name || account.username || account.given_name || account.family_name || 'User',
          email: email,
          initials: (account.name || account.given_name || account.preferred_username || account.email || 'U')
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
        });
      } else {
        setUser(null);
      }
      
      setError(null);
    } catch (err) {
      logger.error('AUTH-STATE', 'Error checking authentication status', { error: err.message });
      setError(err);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [instance, accounts, inProgress]);

  // Handle authentication state changes
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus, accounts, inProgress]);

  // Listen for storage changes (logout from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'msal.account.keys' || e.key === 'msal.interaction.status') {
        logger.debug('AUTH-STATE', 'Authentication state changed in another tab');
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuthStatus]);

  // Listen for focus events (user returned to tab)
  useEffect(() => {
    const handleFocus = () => {
      logger.debug('AUTH-STATE', 'Window focused, checking authentication status');
      checkAuthStatus();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkAuthStatus]);

  // Force refresh authentication state
  const refreshAuthState = useCallback(() => {
    logger.debug('AUTH-STATE', 'Force refreshing authentication state');
    setIsLoading(true);
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Clear authentication state
  const clearAuthState = useCallback(() => {
    logger.debug('AUTH-STATE', 'Clearing authentication state');
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    refreshAuthState,
    clearAuthState,
    checkAuthStatus
  };
};

